/**
 * `/settings/team` — the firm-user admin list (the Sprint-1 T-1.16-era surface,
 * materialised here for the owed-#15 "Reset TOTP" action). Renders under the
 * `_settings` layout (managing_partner | firm_admin — the `users.read` holders);
 * other roles are redirected by the layout guard (§9).
 *
 * "Reset TOTP" mirrors the SERVER gate exactly (CC-2): `users.deactivate` via the
 * ENG-10 catalogue PLUS the managing_partner-only check (the DD-028 "never act
 * upward" posture — a firm_admin sees the list but no reset affordance). The
 * action clears the target's enrolment so their next sign-in re-enrols; a confirm
 * dialog guards against a mis-click.
 *
 * Built on the design-system primitives (UI/UX Flow 29).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldOff, UserPlus, Users, Duotone } from '../../components/ui/icons.ts';
import { useState, type JSX } from 'react';

import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Modal,
  Notice,
  PageHeader,
  SkeletonText,
  Table,
  Td,
  Th,
} from '../../components/ui/index.ts';
import { InviteMemberModal } from '../../components/team/invite-member-modal.tsx';
import { INVITATIONS_KEY, PendingInvitations } from '../../components/team/pending-invitations.tsx';
import { api, getErrorMessage, type FirmUser } from '../../lib/api.ts';
import { describeError, getRequestId, type DescribedError } from '../../lib/request-id.ts';
import { formatRole } from '../../lib/format/role.ts';
import { usePermission } from '../../lib/permissions/index.tsx';
import { useAuth } from '../../store/auth-store.ts';

import '../../styles/settings.css';

const USERS_KEY = ['users', 'list'] as const;

export function TeamScreen(): JSX.Element {
  const queryClient = useQueryClient();
  // The UI affordance mirrors the server gate: users.deactivate (catalogue, CC-2)
  // + managing_partner-only (the routes' `_partner-only` role idiom — DD-028).
  const canDeactivate = usePermission('users.deactivate');
  const isMp = useAuth((s) => s.user?.role === 'managing_partner');
  const canResetTotp = canDeactivate && isMp;
  const canInvite = usePermission('users.invite');
  const inviterRole = useAuth((s) => s.user?.role);
  const [inviteOpen, setInviteOpen] = useState(false);

  const usersQuery = useQuery<FirmUser[]>({
    queryKey: USERS_KEY,
    queryFn: () => api.users.list(),
  });
  const usersList = usersQuery.data;

  const [confirmFor, setConfirmFor] = useState<FirmUser | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<DescribedError | null>(null);

  const resetMutation = useMutation({
    mutationFn: (userId: string) => api.users.resetTotp(userId),
    onSuccess: async (_res, userId) => {
      const target = usersList?.find((u) => u.user_id === userId);
      setStatus(
        `Two-factor reset for ${target?.full_name ?? 'user'}. They re-enrol at next sign-in.`,
      );
      setError(null);
      setConfirmFor(null);
      await queryClient.invalidateQueries({ queryKey: USERS_KEY });
    },
    onError: (err) => {
      setError(describeError(err));
      setStatus(null);
      setConfirmFor(null);
    },
  });

  return (
    <div className="argus-page">
      <PageHeader
        title="Team"
        description={
          canResetTotp
            ? 'Firm users. Resetting two-factor forces the user to re-enrol an authenticator at their next sign-in.'
            : 'Firm users. Only a managing partner can reset two-factor authentication.'
        }
      />

      {status ? <Notice tone="success">{status}</Notice> : null}
      {error ? (
        <ErrorState
          title="Two-factor authentication was not reset"
          message={error.message}
          requestId={error.requestId}
        />
      ) : null}

      <Card>
        <CardHeader
          title="Firm users"
          description="Everyone with access to this firm's workspace, with their role and two-factor state."
          actions={
            <>
              {usersList ? (
                <Badge tone="muted" icon={<Users size={12} aria-hidden="true" />}>
                  {usersList.length === 1 ? '1 user' : `${String(usersList.length)} users`}
                </Badge>
              ) : null}
              {canInvite ? (
                <Button
                  size="sm"
                  icon={<UserPlus size={14} aria-hidden="true" />}
                  onClick={() => {
                    setStatus(null);
                    setInviteOpen(true);
                  }}
                >
                  Invite member
                </Button>
              ) : null}
            </>
          }
        />
        <CardBody flush>
          {usersQuery.isPending ? (
            <div className="argus-settings-inset">
              <SkeletonText lines={4} label="Loading team" />
            </div>
          ) : usersQuery.isError ? (
            <div className="argus-settings-inset">
              <ErrorState
                title="We couldn't load the team"
                message={getErrorMessage(usersQuery.error)}
                requestId={getRequestId(usersQuery.error)}
                onRetry={() => void usersQuery.refetch()}
                retrying={usersQuery.isFetching}
              />
            </div>
          ) : usersQuery.data.length === 0 ? (
            <EmptyState
              icon={<Duotone.Users size={22} aria-hidden="true" />}
              title="No users yet"
              description="People appear here once they accept an invitation to this firm."
            />
          ) : (
            <Table aria-label="Firm users" dense>
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Email</Th>
                  <Th>Role</Th>
                  <Th>Status</Th>
                  <Th>2FA</Th>
                  {canResetTotp ? <Th className="argus-settings-row-actions">Actions</Th> : null}
                </tr>
              </thead>
              <tbody>
                {usersQuery.data.map((u) => (
                  <tr key={u.user_id}>
                    <Td>{u.full_name}</Td>
                    <Td>{u.email}</Td>
                    <Td>{formatRole(u.role)}</Td>
                    <Td>
                      {u.is_active ? (
                        <Badge tone="success">Active</Badge>
                      ) : (
                        <Badge tone="muted">Inactive</Badge>
                      )}
                    </Td>
                    <Td>
                      {u.totp_enrolled ? (
                        <Badge tone="success">Enrolled</Badge>
                      ) : (
                        <Badge tone="warning">Not enrolled</Badge>
                      )}
                    </Td>
                    {canResetTotp ? (
                      <Td className="argus-settings-row-actions">
                        <Button
                          size="sm"
                          variant="danger-outline"
                          icon={<ShieldOff size={14} aria-hidden="true" />}
                          onClick={() => {
                            setStatus(null);
                            setError(null);
                            setConfirmFor(u);
                          }}
                        >
                          Reset TOTP
                        </Button>
                      </Td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      <PendingInvitations canRevoke={canInvite} />

      {inviteOpen ? (
        <InviteMemberModal
          inviterRole={inviterRole}
          onClose={() => setInviteOpen(false)}
          onInvited={(inv) => {
            setInviteOpen(false);
            setStatus(`Invitation sent to ${inv.email}`);
            setError(null);
            void queryClient.invalidateQueries({ queryKey: INVITATIONS_KEY });
          }}
        />
      ) : null}

      {confirmFor ? (
        <Modal
          open
          onClose={() => setConfirmFor(null)}
          role="alertdialog"
          dismissOnOverlay={false}
          hideClose
          title="Confirm TOTP reset"
          footer={
            <>
              <Button
                variant="ghost"
                onClick={() => setConfirmFor(null)}
                disabled={resetMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                loading={resetMutation.isPending}
                onClick={() => resetMutation.mutate(confirmFor.user_id)}
              >
                Confirm reset
              </Button>
            </>
          }
        >
          <p>
            Reset two-factor authentication for <strong>{confirmFor.full_name}</strong> (
            {confirmFor.email})? Their authenticator stops working immediately and they must enrol a
            new one at their next sign-in. This is recorded in the audit trail.
          </p>
        </Modal>
      ) : null}
    </div>
  );
}
